import anime from "animejs";

/**
 * Creates a shaking animation for tension mode
 * Uses percentage-based values relative to symbolHeight for responsive scaling
 * @param {Object} target - PixiJS Container or DOM element to animate
 * @param {number} symbolHeight - Current symbol height for responsive scaling
 * @returns {Object} animejs animation instance
 */
export const createTensionShakeAnimation = (target, symbolHeight) => {
  if (!target || !symbolHeight) return null;

  // Always get current position - don't rely on stored values
  const originalX = target.x || 0;
  const originalY = target.y || 0;

  // Store current position as original for revert animation
  target._originalX = originalX;
  target._originalY = originalY;

  // Use percentage-based shake values (0.075 = 7.5% of symbolHeight)
  // This ensures shake scales proportionally with screen size
  const shakeAmount = symbolHeight * 0.02; // 7.5% of symbol height (reduced from 0.15 for less dramatic effect)
  const shakeMultiplier = 1.5; // Additional intensity multiplier
  const finalShakeAmount = shakeAmount * shakeMultiplier;

  // Convert CSS keyframes to animejs keyframes
  // CSS uses translate(px, px) rotate(deg), animejs uses x, y, rotate
  // Keyframes from CSS @keyframes shake (0%, 10%, 20%, ..., 100%)
  // Using percentage-based values for responsive scaling
  const keyframes = [
    {
      x: originalX + finalShakeAmount * 0.33,
      y: originalY + finalShakeAmount * 0.33,
      rotate: 0,
    }, // 0%
    {
      x: originalX - finalShakeAmount * 0.33,
      y: originalY - finalShakeAmount * 0.67,
      rotate: -1 * shakeMultiplier,
    }, // 10%
    {
      x: originalX - finalShakeAmount,
      y: originalY + 0,
      rotate: 1 * shakeMultiplier,
    }, // 20%
    {
      x: originalX + finalShakeAmount,
      y: originalY + finalShakeAmount * 0.67,
      rotate: 0,
    }, // 30%
    {
      x: originalX + finalShakeAmount * 0.33,
      y: originalY - finalShakeAmount * 0.33,
      rotate: 1 * shakeMultiplier,
    }, // 40%
    {
      x: originalX - finalShakeAmount * 0.33,
      y: originalY + finalShakeAmount * 0.67,
      rotate: -1 * shakeMultiplier,
    }, // 50%
    {
      x: originalX - finalShakeAmount,
      y: originalY + finalShakeAmount * 0.33,
      rotate: 0,
    }, // 60%
    {
      x: originalX + finalShakeAmount,
      y: originalY + finalShakeAmount * 0.33,
      rotate: -1 * shakeMultiplier,
    }, // 70%
    {
      x: originalX - finalShakeAmount * 0.33,
      y: originalY - finalShakeAmount * 0.33,
      rotate: 1 * shakeMultiplier,
    }, // 80%
    {
      x: originalX + finalShakeAmount * 0.33,
      y: originalY + finalShakeAmount * 0.67,
      rotate: 0,
    }, // 90%
    {
      x: originalX + finalShakeAmount * 0.33,
      y: originalY - finalShakeAmount * 0.67,
      rotate: -1 * shakeMultiplier,
    }, // 100%
  ];

  // Calculate keyframe offsets (0%, 10%, 20%, etc.)
  const offsets = keyframes.map((_, i) => (i / (keyframes.length - 1)) * 100);

  return anime({
    targets: target,
    x: keyframes.map((kf) => kf.x),
    y: keyframes.map((kf) => kf.y),
    rotate: keyframes.map((kf) => kf.rotate),
    duration: 500, // 0.5s as in CSS
    easing: "linear",
    keyframes: offsets.map((offset, i) => ({
      offset: offset / 100,
      x: keyframes[i].x,
      y: keyframes[i].y,
      rotate: keyframes[i].rotate,
    })),
    loop: true, // Infinite loop like CSS animation-iteration-count: infinite
    update: () => {
      // Update original position reference on each frame to handle resize
      if (target._originalX === undefined || target._originalY === undefined) {
        target._originalX = originalX;
        target._originalY = originalY;
      }
    },
  });
};
